import { Controller } from "@hotwired/stimulus";

/**
 * Unsaved changes guard.
 *
 * Wrap any form area with this controller. When `dirtyValue` is true,
 * the browser's native "Leave site?" confirmation appears before
 * navigating away or closing the tab, and Turbo Drive in-app visits
 * are intercepted with a confirm prompt.
 *
 * Usage:
 *   <div data-controller="ui--unsaved-changes"
 *        data-ui--unsaved-changes-dirty-value="false">
 *     <!-- form content -->
 *     <!-- fire data-action="ui--unsaved-changes#markDirty" on changes -->
 *   </div>
 */
export default class extends Controller {
  static values = { dirty: { type: Boolean, default: false } };

  connect() {
    this._handleBeforeUnload = this._handleBeforeUnload.bind(this);
    this._handleBeforeVisit = this._handleBeforeVisit.bind(this);
    this._handleClick = this._handleClick.bind(this);
    window.addEventListener("beforeunload", this._handleBeforeUnload);
    document.addEventListener("turbo:before-visit", this._handleBeforeVisit);
    // The admin layout sets data-turbo="false", so turbo:before-visit never fires and beforeunload is
    // unreliable (browsers suppress it without prior interaction) — intercept the click itself.
    document.addEventListener("click", this._handleClick, true);
  }

  disconnect() {
    window.removeEventListener("beforeunload", this._handleBeforeUnload);
    document.removeEventListener("turbo:before-visit", this._handleBeforeVisit);
    document.removeEventListener("click", this._handleClick, true);
  }

  markDirty() {
    this.dirtyValue = true;
  }

  clearDirty() {
    this.dirtyValue = false;
  }

  // --- private ---

  _handleBeforeUnload(event) {
    if (this.dirtyValue) {
      event.preventDefault();
      event.returnValue = "";
    }
  }

  _handleBeforeVisit(event) {
    if (!this.dirtyValue) return;

    if (this._confirmLeave()) {
      this.dirtyValue = false;
    } else {
      event.preventDefault();
    }
  }

  _handleClick(event) {
    if (!this.dirtyValue) return;
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    const link = event.target.closest("a[href]");
    if (!link) return;
    if (link.target === "_blank" || link.hasAttribute("download")) return;
    const href = link.getAttribute("href");
    if (!href || href.startsWith("#")) return;
    // Frame-targeted links update in place rather than leaving the page, so they aren't a navigation.
    const frame = link.dataset.turboFrame || link.closest("turbo-frame")?.id;
    if (frame && frame !== "_top") return;

    if (this._confirmLeave()) {
      this.dirtyValue = false;
    } else {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  _confirmLeave() {
    return window.confirm("You have unsaved changes. Are you sure you want to leave?");
  }
}
